import React from 'react';
import {useTranslation} from 'next-i18next';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Heading,
	Input,
	InputGroup,
	InputLeftElement,
	SimpleGrid,
	Stack,
	Text,
	VStack,
} from '@tempo/core';
import {ArrowRightIcon, InstagramIcon, FacebookIcon, XIcon} from '@tempo/icons';
import type {ArtistFormData, FormFieldChange} from '../../types';
import {ContentType} from '@deezer/next/services/types/contentType';
import Thumbnail from '@components/Thumbnail';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import FooterRow from '@components/PageElements/FooterRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import FormContainer from '@components/PageElements/FormContainer';
import FormSection from '@components/PageElements/FormSection';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';
import {typographyOverrides} from '@deezer/next/shared/utils/typographyOverrides';

type MoreInfoArtistFormCardProps = {
	formData: ArtistFormData;
	onFormFieldChange: FormFieldChange<ArtistFormData>;
	onBack: () => void;
	onContinue: () => void;
	onSubmit: () => void;
	hasDiscographyRights: boolean;
};

const ArtistSocialFormCard = ({
	formData,
	onFormFieldChange,
	onBack,
	onContinue,
	onSubmit,
	hasDiscographyRights,
}: MoreInfoArtistFormCardProps) => {
	const {t} = useTranslation('d4c');

	const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onFormFieldChange('displayName', e.target.value);
	const handleInstagramChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onFormFieldChange('instagram', e.target.value);
	const handleFacebookChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onFormFieldChange('facebook', e.target.value);
	const handleTwitterChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onFormFieldChange('twitter', e.target.value);
	const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onFormFieldChange('website', e.target.value);

	return (
		<PageLayout
			data-testid="artist-socials-card"
			header={<TitleRow title={t('requestaccess_title_newrequest_d4c-web')} />}
			footer={
				<FooterRow
					rightElements={
						<>
							<Button
								data-testid="back-button"
								variant="outline"
								onClick={onBack}
								leftIcon={<ArrowRightIcon transform="scaleX(-1)" />} // TODO fix that
								size="small"
							>
								{t('generic_action_back_d4c-web')}
							</Button>
							<Button
								data-testid={
									hasDiscographyRights ? 'continue-button' : 'submit-button'
								}
								onClick={hasDiscographyRights ? onContinue : onSubmit}
								size="small"
							>
								{hasDiscographyRights
									? t('generic_action_continue_d4c-web')
									: t('generic_action_sendrequest_d4c-web')}
							</Button>
						</>
					}
				/>
			}
		>
			<Box>
				<Box bg="background.neutral.primary.disabled" height="83px" />

				<Stack
					direction={{base: 'column', lg: 'row'}}
					position="relative"
					px="spacing.3xl"
					spacing={{base: 'spacing.m', lg: 'spacing.xl'}}
					alignItems={{base: 'center', lg: 'flex-start'}}
					top="-48px"
				>
					<Box position="relative">
						<Thumbnail
							coverLink={false}
							type={ContentType.Artist}
							height={{base: '96px', md: '144px'}}
							width={{base: '96px', md: '144px'}}
							picture={formData.artist?.picture}
							rounded={true}
						/>
					</Box>
					<Stack
						direction={{base: 'column', lg: 'row'}}
						flex={1}
						position="relative"
						pt={{base: 0, lg: 'spacing.m'}}
						justifyContent="space-between"
						alignItems="center"
						top={{base: 0, lg: '36px'}}
					>
						<VStack
							spacing={{base: 'spacing.s', lg: 'spacing.s'}}
							alignItems={{base: 'center', lg: 'flex-start'}}
						>
							<Heading
								as="h1"
								noOfLines={{base: 3, sm: 2}}
								sx={typographyOverrides['display.s']}
								data-testid={'campaign_manager_album_name_edit'}
								textAlign={{base: 'center', lg: 'left'}}
							>
								{t('requestaccessartist_title_helloXartistname_d4c-web', {
									sprintf: [formData.artist?.name],
								})}
							</Heading>
						</VStack>
					</Stack>
				</Stack>
			</Box>

			<FormContainer pt={0}>
				<FormSection>
					<FormSectionTitle
						title={t(
							'requestaccessartist_subtitle_completeyourprofile_d4c-web',
						)}
					/>
					{hasDiscographyRights && (
						<Text
							variant="body.default.regular"
							color="text.neutral.secondary.default"
						>
							{t('requestaccessartist_text_displayednamedescription_d4c-web')}
						</Text>
					)}
					<FormSectionContainer>
						<VStack spacing="spacing.s">
							{hasDiscographyRights && (
								<FormControl>
									<FormLabel>
										{t('requestaccessartist_title_displayedname_d4c-web')}
									</FormLabel>
									<Input
										id="display-name"
										type="text"
										value={formData.displayName}
										onChange={handleDisplayNameChange}
									/>
								</FormControl>
							)}
							<SimpleGrid
								columns={{base: 1, md: 2}}
								spacing="spacing.l"
								w="100%"
							>
								<FormControl>
									<FormLabel htmlFor="instagram">Instagram</FormLabel>
									<InputGroup>
										<InputLeftElement pointerEvents="none">
											<InstagramIcon boxSize={6} />
										</InputLeftElement>
										<Input
											id="instagram"
											type="text"
											placeholder="@"
											value={formData.instagram}
											onChange={handleInstagramChange}
										/>
									</InputGroup>
								</FormControl>
								<FormControl>
									<FormLabel htmlFor="facebook">Facebook</FormLabel>
									<InputGroup>
										<InputLeftElement pointerEvents="none">
											<FacebookIcon boxSize={6} />
										</InputLeftElement>
										<Input
											id="facebook"
											type="text"
											value={formData.facebook}
											onChange={handleFacebookChange}
										/>
									</InputGroup>
								</FormControl>
								<FormControl>
									{/* TODO migrate to X */}
									<FormLabel htmlFor="x">Twitter</FormLabel>
									<InputGroup>
										<InputLeftElement pointerEvents="none">
											<XIcon boxSize={6} />
										</InputLeftElement>
										<Input
											id="twitter"
											type="text"
											placeholder="twitter.com"
											value={formData.twitter}
											onChange={handleTwitterChange}
										/>
									</InputGroup>
								</FormControl>
								<FormControl>
									<FormLabel htmlFor="website">
										{t('requestaccesslabel_text_website_d4c-web')}
									</FormLabel>
									<Input
										id="website"
										type="text"
										placeholder="https://"
										value={formData.website}
										onChange={handleWebsiteChange}
									/>
								</FormControl>
							</SimpleGrid>
						</VStack>
					</FormSectionContainer>
				</FormSection>
			</FormContainer>
		</PageLayout>
	);
};

export default ArtistSocialFormCard;
